import React from "react";

import ExpandableFilters from "../ExpandableFilters";
import { ClearRefinements, connectStateResults } from "react-instantsearch-dom";
import "./ResponsiveFiltersBar.modal.scss";
import "./ResponsiveFiltersBar.scss";
import { extractAppliedFilters } from "src/utils/algoliaFiltersHelper";
import InlineToggleFilter from "../InlineToggleFilter";
import { Button } from "@silversea-ssc/ui-components-react";
import { StateResultsProvided } from "react-instantsearch-core";
import { t, Plural, Trans } from "@lingui/macro";
import { FindYourCruiseFiltersProps } from "components/FindYourCruise";

export interface ResponsiveFiltersBarProps {
  modalIsOpen: boolean;
  modalCloseClick?: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  applyButton: JSX.Element;
}

const specialTypeFilterLabel = {
  AntarcticaBridge: t`Antarctica Bridge`,
  TopCruise: t`Top Cruise`
};

const ResponsiveFiltersBar: React.FC<
  ResponsiveFiltersBarProps & FindYourCruiseFiltersProps
> = ({
  modalIsOpen,
  modalCloseClick,
  showExpeditionFilter,
  specialTypeFilter,
  showShipFilter,
  showDestinationFilter,
  showDurationFilter,
  showOthersFilter,
  showPromosFilter,
  applyButton
}) => {
  return (
    <div
      className={`ssc-responsive-filters ${
        modalIsOpen
          ? "ssc-responsive-filters__modal ssc-responsive-filters__modal--open"
          : ""
      }`}
    >
      <div className="ssc-responsive-filters__modal__header">
        <div className="ssc-responsive-filters__modal__header__title">
          <Trans>Set Filters</Trans>
        </div>
        <Button
          renderAs="button"
          variant="link"
          size="normal"
          iconName="close"
          onClick={modalCloseClick}
        >
          <Trans id="Modal close button label">Close</Trans>
        </Button>
      </div>
      <div className="ssc-responsive-filters__modal__body__scroll">
        <div className="ssc-responsive-filters__modal__body">
          <ExpandableFilters
            className="ssc-filters--filter-group"
            showShipFilter={showShipFilter}
            showDestinationFilter={showDestinationFilter}
            showDurationFilter={showDurationFilter}
            showPromosFilter={showPromosFilter}
          />
          {showOthersFilter && (
            <div className="ssc-filters--filter-group">
              <div className="ssc-filter-inline">
                <div className="ssc-filters--filter-title">
                  <Trans>Others</Trans>
                </div>
                {showExpeditionFilter && (
                  <InlineToggleFilter
                    id="expedition-checkbox"
                    attribute="cruiseType"
                    label={"Expedition Cruise"}
                    value="Expedition"
                    dataContentPiece="expedition_checkbox"
                  />
                )}

                {specialTypeFilter != null && (
                  <InlineToggleFilter
                    id="top-cruises-checkbox"
                    attribute="content.specialType"
                    label={specialTypeFilterLabel[specialTypeFilter]}
                    value={specialTypeFilter}
                    dataContentPiece="top_cruises_checkbox"
                  />
                )}

                <InlineToggleFilter
                  id="available-checkbox"
                  attribute="available"
                  label={t`Available only`}
                  value="true"
                  dataContentPiece="available_checkbox"
                />
                {process.env.GATSBY_SHOW_ALL_INCLUSIVE_FILTER == "true" && (
                  <InlineToggleFilter
                    id="all-inclusive-filter"
                    attribute="hasAllInclusiveFares"
                    label={"Included shore excursions"}
                    value="true"
                    dataContentPiece="all_inclusive_checkbox"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="ssc-responsive-filters__modal__footer">
        <div className="ssc-responsive-filters__modal__footer__filters-buttons">
          <AppliedFilterCount />
          <ClearRefinements
            className="ssc-responsive-filters__button--reset"
            translations={{
              reset: t`Reset Filters`
            }}
          />
        </div>
        {applyButton}
      </div>
    </div>
  );
};

const AppliedFilterCount = connectStateResults(({ searchState }) => {
  const appliedFilters = extractAppliedFilters(searchState);
  const appliedFiltersCount = appliedFilters.counter;

  return (
    <div className="ais-Stats">
      <span className="ais-Stats-text">
        <Plural
          value={appliedFiltersCount}
          one="# Filter Selected"
          other="# Filters Selected"
          zero="No Filters Selected"
        />
      </span>
    </div>
  );
});

interface ApplyButtonProps {
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  label?: (
    appliedFiltersCount?: number,
    resultsFoundCount?: number
  ) => JSX.Element;
}

export const ApplyButton = connectStateResults<
  ApplyButtonProps & StateResultsProvided
>(({ label, onClick, searchResults, searchState }) => {
  const resultsFoundCount = searchResults?.nbHits || 0;
  const appliedFilters = extractAppliedFilters(searchState);
  const appliedFiltersCount = appliedFilters.counter;

  return (
    <Button
      renderAs="button"
      variant="primary"
      className="ssc-responsive-filters__button--close"
      onClick={onClick}
    >
      {label && label(appliedFiltersCount, resultsFoundCount)}
    </Button>
  );
});

export default ResponsiveFiltersBar;
